import type { ApiClient } from "./client";

/** Role taxonomy:
 *   admin  — full access + manages users
 *   viewer — sees every project, no admin powers (default for new users)
 *   user   — restricted to the projects in `custom:projects`
 */
export type Role = "admin" | "viewer" | "user";

export const ROLE_LABEL: Record<Role, string> = {
  admin:  "admin",
  viewer: "viewer",
  user:   "user",
};

export const ROLE_DESCRIPTION: Record<Role, string> = {
  admin:  "Full access. Can change roles and per-user project allowlists.",
  viewer: "Sees every project. Cannot change permissions. Default for new sign-ins.",
  user:   "Restricted to the projects in their allowlist.",
};

export interface Me {
  sub: string;
  username: string;
  email: string;
  name: string;
  role: Role;
  /** ["*"] means all projects. */
  projects: string[];
}

export interface AdminUser {
  username: string;
  sub: string;
  email: string;
  name: string;
  role: Role;
  projects: string[];
  status: string;
  enabled: boolean;
  first_seen: string;
  last_modified: string;
}

interface MeResponse { me: Me }
interface UsersResponse { users: AdminUser[] }
interface UserResponse { user: AdminUser }

export async function fetchMe(client: ApiClient): Promise<Me> {
  return (await client.get<MeResponse>("/me")).me;
}

export async function listUsers(client: ApiClient): Promise<AdminUser[]> {
  return (await client.get<UsersResponse>("/admin/users")).users;
}

export async function patchUser(
  client: ApiClient,
  username: string,
  patch: Partial<Pick<AdminUser, "role" | "projects">>,
): Promise<AdminUser> {
  return (await client.patch<UserResponse>(`/admin/users/${encodeURIComponent(username)}`, patch)).user;
}

// ---------------------------------------------------------------------------
// Onboard a new Jira board onto the Sprint Scorecard pipeline.
//
// Lambda kicks off `f/jira_sprint_scorecard_onboard/main` via Windmill and
// returns the parent job id. The SPA polls the parent for overall status and
// the children list for per-sprint progress; the onboard flow itself fans
// out one child Windmill job per closed/active sprint.
// ---------------------------------------------------------------------------

export interface OnboardKickoffRequest {
  jira_base_url: string;
  board_id: number;
}

export interface OnboardKickoffResponse {
  job_id: string;
  board_id: number;
  jira_base_url: string;
  cooldown_seconds: number;
}

export type OnboardJobStatus = "queued" | "running" | "completed" | "unknown";

export interface OnboardSprintResult {
  sprint_id: number;
  sprint_name: string;
  sprint_state: string;
  job_id: string;
  outcome: "success" | "timeout" | "failure";
}

export interface OnboardFlowResult {
  status: "onboarded" | "already_onboarded";
  schedule_path: string;
  board_id: number;
  sprint_count?: number;
  results?: OnboardSprintResult[];
}

export interface OnboardJob {
  job_id: string;
  status: OnboardJobStatus;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  success?: boolean;
  result?: OnboardFlowResult;
  error_brief?: string;
}

export interface OnboardChildJob {
  job_id: string;
  sprint_id: number | null;
  status: OnboardJobStatus;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  success?: boolean;
}

interface OnboardJobResponse { job: OnboardJob }
interface OnboardChildrenResponse { children: OnboardChildJob[] }

export async function triggerOnboard(
  client: ApiClient,
  body: OnboardKickoffRequest,
): Promise<OnboardKickoffResponse> {
  return await client.post<OnboardKickoffResponse>("/admin/onboard", body);
}

export async function fetchOnboardJob(
  client: ApiClient,
  jobId: string,
): Promise<OnboardJob> {
  return (
    await client.get<OnboardJobResponse>(
      `/admin/onboard/jobs/${encodeURIComponent(jobId)}`,
    )
  ).job;
}

export async function fetchOnboardChildren(
  client: ApiClient,
  jobId: string,
): Promise<OnboardChildJob[]> {
  return (
    await client.get<OnboardChildrenResponse>(
      `/admin/onboard/jobs/${encodeURIComponent(jobId)}/children`,
    )
  ).children;
}

// ---------------------------------------------------------------------------
// Project-level board discovery. Lambda enumerates the project's boards via
// Jira Agile API and annotates each with cheap 30-day activity stats plus an
// already-onboarded flag derived from Windmill's daily_board_<id> schedules.
// See docs/superpowers/specs/2026-06-11-project-level-onboarding-design.md.
// ---------------------------------------------------------------------------

export interface DiscoveredBoard {
  board_id: number;
  name: string;
  /** Lowercased Jira board type; "simple" behaves like kanban downstream. */
  type: string;
  /** Issues touched in the last 30 days; null if the stat call failed/capped. */
  recent_tickets: number | null;
  contributors: number | null;
  /** True when the 50-issue sample may undercount contributors. */
  contributors_lower_bound: boolean;
  /** ISO date of the most recent issue update in the sample, or null. */
  last_activity: string | null;
  /**
   * Project key the board is located under in Jira. Can differ from the
   * requested project: Jira lists any board whose filter includes the
   * project's issues, wherever the board lives. Null if Jira omitted it.
   */
  location_project: string | null;
  /**
   * Saved-filter id backing the board. Boards sharing one filter are
   * duplicate views over the same issues — onboarding both scores the same
   * sprints twice. Null when the lookup failed or was capped.
   */
  filter_id: number | null;
  /** null = Windmill unreachable, treat as unknown. */
  already_onboarded: boolean | null;
}

export interface DiscoverBoardsResponse {
  project: string;
  jira_base_url: string;
  boards: DiscoveredBoard[];
  /** True when stats were only computed for the first N boards. */
  stats_truncated: boolean;
}

export async function discoverBoards(
  client: ApiClient,
  projectKey: string,
): Promise<DiscoverBoardsResponse> {
  return await client.get<DiscoverBoardsResponse>(
    `/admin/boards/discover?project=${encodeURIComponent(projectKey)}`,
  );
}

// ---------------------------------------------------------------------------
// Per-project "From" override for sprint reminder DMs. When set, /remind
// signs the DM as the configured sender instead of the JWT caller; the
// `operator_slack_id` (resolved via Slack users.lookupByEmail at save time)
// makes the signoff render as a clickable mention chip.
// ---------------------------------------------------------------------------

export interface ProjectSender {
  operator_name?: string;
  operator_slack_id?: string;
  operator_slack_email?: string;
}

export interface ProjectSenderResponse {
  project_key: string;
  sender: ProjectSender;
  /** Set when the Slack email lookup failed — config is persisted but the
   *  signoff will fall back to plain text until a resolvable email is set. */
  warning?: string;
}

export async function fetchProjectSender(
  client: ApiClient,
  projectKey: string,
): Promise<ProjectSenderResponse> {
  return await client.get<ProjectSenderResponse>(
    `/admin/projects/${encodeURIComponent(projectKey)}/sender`,
  );
}

export async function setProjectSender(
  client: ApiClient,
  projectKey: string,
  body: { display_name: string; slack_email?: string },
): Promise<ProjectSenderResponse> {
  return await client.put<ProjectSenderResponse>(
    `/admin/projects/${encodeURIComponent(projectKey)}/sender`,
    body,
  );
}
